import React, { useContext, useEffect, useState } from "react";
import { Button } from "theme-ui";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { createServiceArea, getServiceAreas } from "api/ServiceAreas";
import { trackActivity } from "api/ActivityTracker";

import { filterByKeys } from "helpers/default";

import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { useToast } from "components/toast";
import BasicButton from "components/forms/BasicButton";
import { CheckMarkIcon, PlusIcon, ServiceAreaIcon } from "components/svg";
import TextInput from "components/forms/TextInput";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import DrawGoogleMap from "components/forms/DrawGoogleMap";
import ColorPickerField from "components/forms/ColorPickerField";
import { colorForIndex } from "components/ServiceAreaMap";
import UserContext from "context/User";

export default function CreateModal({ includeButton, afterCreate, onToggle, isOpen = false, bottomFix = false, isPrimary = false }: { includeButton?: boolean, bottomFix?: boolean, isPrimary?: boolean, isOpen?: boolean; afterCreate?: (val?: any) => void, onToggle?: (open: boolean) => void }) {

  const toast = useToast();  
  const [isModalOpen, setIsModalOpen] = useState<boolean>(isOpen);
  const { user } = useContext<any>(UserContext);

  // Session 30 — existing service areas fetched on modal open, shown as
  // muted context polygons behind the shape being drawn.
  const [contextPolygons, setContextPolygons] = useState<any[]>([]);
  const [showContext, setShowContext] = useState<boolean>(true);


  const _validate = Yup.object().shape({
    name: Yup.string().required("Service area name is required."),    
  });

  async function _onSubmit(values: any) {  
    const keys = ["name","geometry","color"];    
    const create = filterByKeys(keys,values);

    const res = await createServiceArea({ create });
    if(res?.id){ 
      toast.add("Service area created","var(--theme-ui-colors-success)");      
      void trackActivity({
        module: "service_areas",
        action: "create_service_area",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "service_area",
        entityId: res?.id ?? null,
        metadata: null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setIsModalOpen(false);
      if(typeof afterCreate === 'function') afterCreate(res);
    } else{
      toast.add(res?.message ? res.message : "Error creating service area","var(--theme-ui-colors-danger)");
    }  
  }

  useEffect(() => {
    if(typeof onToggle === 'function') onToggle(isModalOpen);
  }, [isModalOpen]);

  // Session 30 — fetch existing areas (with geometry) once when the modal
  // opens. Cached in local state so toggling the checkbox off/on doesn't
  // refetch. Reset on close so a fresh fetch happens on next open.
  useEffect(() => {
    if (!isModalOpen) return;
    if (contextPolygons.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await getServiceAreas({
          query: { includeGeometry: "true", recordsPer: "500", sortBy: "name", sortDir: "asc" },
        });
        if (!cancelled && Array.isArray(res?.items)) {
          setContextPolygons(res.items);
        }
      } catch {
        // Silent fail — context polygons are a "nice to have", not required.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isModalOpen]);

  return (
    <>
      {includeButton && <BasicButton type="button" $iconHeight="10px" scheme={isPrimary ? "primary" : "secondaryOutline"} styles={bottomFix ? {position:"relative", bottom:"2px"} : {}} onClick={() => setIsModalOpen(!isModalOpen)}><PlusIcon fill={isPrimary ? "#fff" : "#000"}/>Add service area</BasicButton>}
      <Modal size="md" isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} centered>
        <Formik initialValues={{}} onSubmit={_onSubmit} validationSchema={_validate} enableReinitialize>
          {({ isSubmitting, values, setFieldValue, submitCount, errors }: { isSubmitting: boolean, values: any, setFieldValue: (name: string, val?: any) => void, submitCount: number, errors: any }) => { 
            return (                
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
                  <ModalIcon><ServiceAreaIcon fill={"000"}/></ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Add service area</ModalTitle>
                    <ModalSubtitle>{"Add a service area to attach to a provider."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>              
                <ModalBody> 
                  <TextInput
                    name="name"
                    label="Service Area Name"
                    value={values.name}
                    onChange={(val: any) => setFieldValue("name",val)}
                    $errors={errors?.name && submitCount > 0 ? errors?.name : null}    
                  />
                  <ColorPickerField
                    label="Map color"
                    value={values.color}
                    onChange={(val: string | null) => setFieldValue("color", val)}
                    fallbackValue={colorForIndex(contextPolygons.length)}
                    helperText="Optional. Leave blank to use the automatic color."
                  />
                  {contextPolygons.length > 0 && (
                    <div style={{ margin: "8px 0 4px", fontSize: 12, color: "#818181", display: "flex", alignItems: "center", gap: 8 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={showContext}
                          onChange={(e) => setShowContext(e.target.checked)}
                        />
                        Show existing service areas
                      </label>
                      {showContext && (
                        <span>· {contextPolygons.length} area{contextPolygons.length === 1 ? "" : "s"} shown as context</span>
                      )}
                    </div>
                  )}
                  <DrawGoogleMap 
                    label="Service Area"
                    value={values.geometry}
                    onChange={(val: any) => setFieldValue("geometry",val)}
                    contextPolygons={showContext ? contextPolygons : []}
                  />             
                </ModalBody>
                <ModalFooter>
                  <Button
                    variant="secondaryOutline"
                    type="button" 
                    onClick={() => setIsModalOpen(false)} 
                  >Cancel</Button>   
                  <BasicButton
                    type="submit" 
                    $submitting={isSubmitting}
                    $iconHeight="14px"
                    $iconWidth="14px"
                    $loadingColor="#fff"
                    keepText={true}
                  >
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"}/>}
                    Save service area
                  </BasicButton>
                </ModalFooter>            
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
}
